import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Settings2, X, Zap } from 'lucide-react';
import { currentConnection } from 'src/features/Broadcasts/broadcastApi';
import quickReplyApi, { matchReplies, useQuickReplies } from './quickReplyApi';

// AUTO-WA-39: pick a saved answer while chatting. Enter inserts the first match.
export default function QuickReplyPicker({ onInsert, onClose }) {
  const workspaceId = currentConnection()?.workspaceId;
  const [state] = useQuickReplies(workspaceId);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const items = matchReplies(state.items, query);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    const onPointer = (event) => { if (!rootRef.current?.contains(event.target)) onClose(); };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [onClose]);

  const choose = (item) => {
    if (!item) return;
    onInsert(item.body);
    quickReplyApi.used(workspaceId, item.id);
    onClose();
  };
  const onKey = (event) => {
    if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((index) => Math.min(items.length - 1, index + 1)); }
    if (event.key === 'ArrowUp') { event.preventDefault(); setActive((index) => Math.max(0, index - 1)); }
    if (event.key === 'Enter') { event.preventDefault(); choose(items[active]); }
  };

  return (
    <div ref={rootRef} className="wa-qr-picker" role="dialog" aria-label="Quick replies">
      <div className="wa-qr-head">
        <Zap size={15} aria-hidden="true" />
        <strong>Quick replies</strong>
        <Link to="/settings/quick-replies" className="wa-qr-manage" onClick={onClose}><Settings2 size={13} />Manage</Link>
        <button type="button" className="ws-row-more" aria-label="Close quick replies" onClick={onClose}><X size={14} /></button>
      </div>
      <label className="ws-searchbox wa-qr-search">
        <Search size={14} aria-hidden="true" />
        <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={onKey} placeholder="Search saved answers" aria-label="Search quick replies" />
      </label>
      <ul className="wa-qr-list" role="listbox" aria-label="Saved answers">
        {state.loading && <li className="wa-qr-note">Loading…</li>}
        {!state.loading && state.error && <li className="wa-qr-note">{state.error}</li>}
        {!state.loading && !state.error && state.items.length === 0 && (
          <li className="wa-qr-note">
            <strong>No quick replies yet</strong>
            Save answers you type often, like store timings or your price list.
            <Link to="/settings/quick-replies" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={onClose} style={{ marginTop: 8 }}>Add quick replies</Link>
          </li>
        )}
        {!state.loading && state.items.length > 0 && items.length === 0 && <li className="wa-qr-note">Nothing matches “{query}”.</li>}
        {items.map((item, index) => (
          <li key={item.id} role="option" aria-selected={index === active}>
            <button type="button" className={`wa-qr-item${index === active ? ' is-active' : ''}`} onMouseEnter={() => setActive(index)} onClick={() => choose(item)}>
              <span className="wa-qr-item-top"><strong>{item.title}</strong><code>/{item.shortcut}</code></span>
              <span className="wa-qr-item-body">{item.body}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
